import React, { useRef, useEffect } from "react";
import { X } from "lucide-react";
import DatePicker from "../Common/DatePicker";

/**
 * Report Filters popup – same layout and behavior as Filters.jsx:
 * - Same dimensions: w-[95%] max-w-6xl h-[80vh]
 * - Green border and label when a field is changed from default (like Filters.jsx)
 */
export default function ReportFiltersPopup({
  darkMode = false,
  filters = {},
  onFilterChange,
  onClose,
  onClear,
  onApply,
  applyLoading = false,
  branches = [],
  departments = [],
  designations = [],
  grades = [],
  categories = [],
  subDepartments = [],
  reportingManagers = [],
}) {
  const modalRef = useRef(null);

  const isFieldSelected = (fieldName, value) => {
    if (value === undefined || value === null) return false;
    const v = String(value).trim();
    if (v === "") return false;
    if (fieldName === "dateFilterType") return v !== "all";
    return true;
  };

  const getFieldBorderClass = (fieldName, value) => {
    if (isFieldSelected(fieldName, value)) return "border-green-500";
    return "";
  };

  const getFieldTextClass = (fieldName, value) => {
    if (!isFieldSelected(fieldName, value)) return "";
    return darkMode ? "text-green-300" : "text-green-700";
  };

  const getLabelTextClass = (fieldName, value) => {
    if (isFieldSelected(fieldName, value)) {
      return darkMode ? "text-green-300" : "text-green-700";
    }
    return darkMode ? "text-gray-400" : "text-gray-500";
  };

  const handleChange = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    if (onFilterChange) onFilterChange(key, value);
  };

  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !e.target.matches("input, textarea, select")) {
        if (typeof onClose === "function") onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const baseInputClass = "block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-sm border appearance-none focus:outline-none focus:ring-0 peer ";
  const baseLabelClass = "absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ";
  const darkInput = darkMode ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]" : "bg-white border-gray-300 text-gray-900";
  const darkLabelBg = darkMode ? "bg-[#3C3C3C]" : "bg-white";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Report Filters"
    >
      <div
        ref={modalRef}
        className={`w-[95%] max-w-6xl h-[80vh] rounded-none shadow-xl flex flex-col overflow-hidden ${
          darkMode ? "bg-[#2C2C2C]" : "bg-white"
        }`}
      >
        <div className={`flex justify-between items-center p-6 border-b flex-shrink-0 ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
          <h2 className={`text-xl font-semibold ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>
            Filter
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-none hover:bg-gray-100 ${darkMode ? "text-[#A6A9C8] hover:bg-[#3C3C3C]" : "text-gray-500"}`}
            aria-label="Close filters"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
          {/* Master Data */}
          <div className="mb-8">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>Master Data</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { key: "branch", label: "Branch", options: branches, optionLabel: "name" },
                { key: "department", label: "Department", options: departments, optionLabel: "name" },
                { key: "designation", label: "Designation", options: designations, optionLabel: "name" },
                { key: "grade", label: "Grade", options: grades, optionLabel: "name" },
                { key: "category", label: "Category", options: categories, optionLabel: "name" },
                { key: "subDepartment", label: "Sub Department", options: subDepartments, optionLabel: "name" },
              ].map(({ key, label, options, optionLabel }) => (
                <div key={key} className="relative">
                  <select
                    value={filters[key] || ""}
                    onChange={handleChange(key)}
                    className={`${baseInputClass} ${darkInput} ${getFieldBorderClass(key, filters[key])} ${getFieldTextClass(key, filters[key])}`}
                  >
                    <option value="">All {label}s</option>
                    {(options || []).map((o) => (
                      <option key={o.id} value={o.id}>{o[optionLabel] || o.name}</option>
                    ))}
                  </select>
                  <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass(key, filters[key])}`}>{label}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Employment Details */}
          <div className="mb-8">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>Employment Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="relative">
                <select
                  value={filters.employmentType || ""}
                  onChange={handleChange("employmentType")}
                  className={`${baseInputClass} ${darkInput} ${getFieldBorderClass("employmentType", filters.employmentType)} ${getFieldTextClass("employmentType", filters.employmentType)}`}
                >
                  <option value="">All Types</option>
                  <option value="full-time">Full Time</option>
                  <option value="external">External</option>
                </select>
                <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass("employmentType", filters.employmentType)}`}>Employment Type</label>
              </div>
              <div className="relative">
                <select
                  value={filters.status || ""}
                  onChange={handleChange("status")}
                  className={`${baseInputClass} ${darkInput} ${getFieldBorderClass("status", filters.status)} ${getFieldTextClass("status", filters.status)}`}
                >
                  <option value="">All Status</option>
                  <option value="probation">Probation</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="resigned">Resigned</option>
                  <option value="relieved">Relieved</option>
                  <option value="terminated">Terminated</option>
                </select>
                <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass("status", filters.status)}`}>Employment Status</label>
              </div>
            </div>
          </div>

          {/* Search & User Filter */}
          <div className="mb-8">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>Search & User Filter</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="relative md:col-span-2 lg:col-span-3">
                <input
                  type="text"
                  name="employee_filter_query"
                  id="admin-report-filters-search"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  data-lpignore="true"
                  value={filters.search || ""}
                  onChange={handleChange("search")}
                  placeholder="Search by name, phone, employee code, PAN, or Aadhaar"
                  className={`block px-3 py-2 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 ${darkInput} ${getFieldBorderClass("search", filters.search)} ${getFieldTextClass("search", filters.search)}`}
                />
              </div>
              <div className="relative">
                <select
                  value={filters.gender || ""}
                  onChange={handleChange("gender")}
                  className={`${baseInputClass} ${darkInput} ${getFieldBorderClass("gender", filters.gender)} ${getFieldTextClass("gender", filters.gender)}`}
                >
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass("gender", filters.gender)}`}>Gender</label>
              </div>
              <div className="relative">
                <select
                  value={filters.userStatus || ""}
                  onChange={handleChange("userStatus")}
                  className={`${baseInputClass} ${darkInput} ${getFieldBorderClass("userStatus", filters.userStatus)} ${getFieldTextClass("userStatus", filters.userStatus)}`}
                >
                  <option value="">All User Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass("userStatus", filters.userStatus)}`}>User Status</label>
              </div>
              <div className="relative">
                <select
                  value={filters.bloodGroup || ""}
                  onChange={handleChange("bloodGroup")}
                  className={`${baseInputClass} ${darkInput} ${getFieldBorderClass("bloodGroup", filters.bloodGroup)} ${getFieldTextClass("bloodGroup", filters.bloodGroup)}`}
                >
                  <option value="">All Blood Groups</option>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
                <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass("bloodGroup", filters.bloodGroup)}`}>Blood Group</label>
              </div>
              <div className="relative">
                <select
                  value={filters.reportingManagerId || ""}
                  onChange={handleChange("reportingManagerId")}
                  className={`${baseInputClass} ${darkInput} ${getFieldBorderClass("reportingManagerId", filters.reportingManagerId)} ${getFieldTextClass("reportingManagerId", filters.reportingManagerId)}`}
                >
                  <option value="">All Reporting Managers</option>
                  {(reportingManagers || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.user?.name || m.name || m.empCode || m.id}</option>
                  ))}
                </select>
                <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass("reportingManagerId", filters.reportingManagerId)}`}>Reporting Manager</label>
              </div>
            </div>
          </div>

          {/* Date Filter */}
          <div className="mb-8">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>Date Filter</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="relative">
                <select
                  value={filters.dateFilterType || "all"}
                  onChange={handleChange("dateFilterType")}
                  className={`${baseInputClass} ${darkInput} ${getFieldBorderClass("dateFilterType", filters.dateFilterType)} ${getFieldTextClass("dateFilterType", filters.dateFilterType)}`}
                >
                  <option value="all">All</option>
                  <option value="dateOfJoin">Date of Joining</option>
                  <option value="dateOfRelieving">Date of Relieving</option>
                  <option value="confirmationDate">Confirmation Date</option>
                  <option value="resignationDate">Resignation Date</option>
                  <option value="terminationDate">Termination Date</option>
                  <option value="dateOfBirth">Date of Birth</option>
                </select>
                <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass("dateFilterType", filters.dateFilterType)}`}>Date Filter Type</label>
              </div>
              <DatePicker
                value={filters.reportDateFrom || ""}
                onChange={(e) => onFilterChange && onFilterChange("reportDateFrom", e?.target?.value ?? "")}
                disabled={filters.dateFilterType === "all"}
                darkMode={darkMode}
                label="From Date"
                max={filters.reportDateTo || ""}
                className={getFieldBorderClass("reportDateFrom", filters.reportDateFrom)}
              />
              <DatePicker
                value={filters.reportDateTo || ""}
                onChange={(e) => onFilterChange && onFilterChange("reportDateTo", e?.target?.value ?? "")}
                disabled={filters.dateFilterType === "all"}
                darkMode={darkMode}
                label="To Date"
                min={filters.reportDateFrom}
                className={getFieldBorderClass("reportDateTo", filters.reportDateTo)}
              />
            </div>
          </div>

          {/* Statutory Applicability */}
          <div className="mb-8">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>Statutory Applicability</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { key: "pfApplicable", label: "PF Applicable" },
                { key: "epsApplicable", label: "EPS Applicable" },
                { key: "esiApplicable", label: "ESI Applicable" },
                { key: "ceilingPf", label: "Ceiling PF" },
                { key: "hasUan", label: "Has UAN" },
                { key: "hasEsiIp", label: "Has ESI IP" },
                { key: "ptApplicable", label: "PT Applicable" },
                { key: "gratuityApplicable", label: "Gratuity Applicable" },
              ].map(({ key, label }) => (
                <div key={key} className="relative">
                  <select
                    value={filters[key] || ""}
                    onChange={handleChange(key)}
                    className={`${baseInputClass} ${darkInput} ${getFieldBorderClass(key, filters[key])} ${getFieldTextClass(key, filters[key])}`}
                  >
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  <label className={`${baseLabelClass} ${darkLabelBg} ${getLabelTextClass(key, filters[key])}`}>{label}</label>
                </div>
              ))}
            </div>
          </div>

          {/* CTC Range */}
          <div className="mb-8">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>CTC Range</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-[#E0E0E0]" : "text-gray-700"} ${getFieldTextClass("ctcFrom", filters.ctcFrom)}`}>CTC From (₹)</label>
                <input
                  type="number"
                  value={filters.ctcFrom ?? ""}
                  onChange={handleChange("ctcFrom")}
                  placeholder="Enter Minimum CTC"
                  className={`block w-full px-3 py-2 text-sm rounded-none border focus:outline-none focus:ring-0 ${darkInput} ${getFieldBorderClass("ctcFrom", filters.ctcFrom)} ${getFieldTextClass("ctcFrom", filters.ctcFrom)}`}
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-[#E0E0E0]" : "text-gray-700"} ${getFieldTextClass("ctcTo", filters.ctcTo)}`}>CTC To (₹)</label>
                <input
                  type="number"
                  value={filters.ctcTo ?? ""}
                  onChange={handleChange("ctcTo")}
                  placeholder="Enter Maximum CTC"
                  className={`block w-full px-3 py-2 text-sm rounded-none border focus:outline-none focus:ring-0 ${darkInput} ${getFieldBorderClass("ctcTo", filters.ctcTo)} ${getFieldTextClass("ctcTo", filters.ctcTo)}`}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Action Buttons - same as Filters.jsx */}
        <div className={`flex justify-between items-center p-6 border-t flex-shrink-0 ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
          <div className="min-w-0 pr-4" />
          <div className="flex space-x-3">
            <button
              onClick={onClear}
              className={`${darkMode ? "bg-[#4C4C4C] text-[#E0E0E0]" : "bg-gray-200 text-gray-800"} px-6 py-3 rounded-none font-medium`}
            >
              Clear
            </button>
            <button
              onClick={onApply}
              disabled={applyLoading}
              className={`${darkMode ? "bg-[#BB86FC] text-white" : "rh-btn-primary"} ${applyLoading ? "opacity-70 cursor-not-allowed" : ""} px-6 py-3 rounded-none font-medium`}
            >
              {applyLoading ? "Applying..." : "Apply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
